import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getSorteos,
  getSorteo,
  crearSorteo,
  actualizarSorteo,
  eliminarSorteo,
  getLaboratoriosSorteos,
  getParticipantes,
  crearParticipante,
  cargarExcelParticipantes,
  eliminarParticipante,
  eliminarParticipantes,
  getGanadores,
  registrarGanador,
  crearYGirar,
  resetearGanadores,
  eliminarGanador,
  deshacerUltimoGanador,
  descargarGanadores,
  getPremios,
  getPremioActivo,
  crearPremio,
  actualizarPremio,
  eliminarPremio,
} from '../api/sorteos.api'
import type {
  FiltrosSorteos,
  SorteoCreatePayload,
  SorteoUpdatePayload,
  GanadorCreatePayload,
  PremioCreatePayload,
  PremioUpdatePayload,
} from '../types/sorteos.types'

// === LABORATORIOS ===

export function useLaboratoriosSorteos() {
  return useQuery({
    queryKey: ['sorteos-laboratorios'],
    queryFn: getLaboratoriosSorteos,
  })
}

// === SORTEOS ===

export function useSorteos(params?: FiltrosSorteos) {
  return useQuery({
    queryKey: ['sorteos', params],
    queryFn: () => getSorteos(params),
  })
}

export function useSorteo(id: string) {
  return useQuery({
    queryKey: ['sorteo', id],
    queryFn: () => getSorteo(id),
    enabled: !!id,
  })
}

export function useCrearSorteo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SorteoCreatePayload) => crearSorteo(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sorteos'] })
      toast.success('Sorteo creado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al crear sorteo')
    },
  })
}

export function useActualizarSorteo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SorteoUpdatePayload }) =>
      actualizarSorteo(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['sorteos'] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', id] })
      toast.success('Sorteo actualizado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al actualizar sorteo')
    },
  })
}

export function useEliminarSorteo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => eliminarSorteo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sorteos'] })
      toast.success('Sorteo eliminado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al eliminar sorteo')
    },
  })
}

// === PARTICIPANTES ===

export function useParticipantes(sorteoId: string) {
  return useQuery({
    queryKey: ['participantes', sorteoId],
    queryFn: () => getParticipantes(sorteoId),
    enabled: !!sorteoId,
  })
}

export function useCrearParticipante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, payload }: { sorteoId: string; payload: { nombre: string; correo?: string; pais?: string } }) =>
      crearParticipante(sorteoId, payload),
    onSuccess: (_, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      toast.success('Participante agregado')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al crear participante')
    },
  })
}

export function useCargarExcel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, archivo }: { sorteoId: string; archivo: File }) =>
      cargarExcelParticipantes(sorteoId, archivo),
    onSuccess: (data, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      toast.success(data.mensaje)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al cargar Excel')
    },
  })
}

export function useEliminarParticipante() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, participanteId }: { sorteoId: string; participanteId: number }) =>
      eliminarParticipante(sorteoId, participanteId),
    onSuccess: (data, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      toast.success(data.mensaje)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al eliminar participante')
    },
  })
}

export function useEliminarParticipantes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sorteoId: string) => eliminarParticipantes(sorteoId),
    onSuccess: (data, sorteoId) => {
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      toast.success(data.mensaje)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al eliminar participantes')
    },
  })
}

// === GANADORES ===

export function useGanadores(sorteoId: string) {
  return useQuery({
    queryKey: ['ganadores', sorteoId],
    queryFn: () => getGanadores(sorteoId),
    enabled: !!sorteoId,
  })
}

export function useRegistrarGanador() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, payload }: { sorteoId: string; payload: GanadorCreatePayload }) =>
      registrarGanador(sorteoId, payload),
    onSuccess: (_, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['ganadores', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premios', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premio-activo', sorteoId] })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al registrar ganador')
    },
  })
}

export function useCrearYGirar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, payload }: { sorteoId: string; payload: { nombre: string; opcion_index: number } }) =>
      crearYGirar(sorteoId, payload),
    onSuccess: (_, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['ganadores', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premios', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premio-activo', sorteoId] })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al girar')
    },
  })
}

export function useResetearGanadores() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, pais }: { sorteoId: string; pais?: string }) =>
      resetearGanadores(sorteoId, pais),
    onSuccess: (data, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['ganadores', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      toast.success(data.mensaje)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al resetear ganadores')
    },
  })
}

export function useEliminarGanador() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, ganadorId }: { sorteoId: string; ganadorId: number }) =>
      eliminarGanador(sorteoId, ganadorId),
    onSuccess: (data, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['ganadores', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premios', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premio-activo', sorteoId] })
      toast.success(data.mensaje)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al eliminar ganador')
    },
  })
}

export function useDeshacerUltimoGanador() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sorteoId: string) => deshacerUltimoGanador(sorteoId),
    onSuccess: (data, sorteoId) => {
      queryClient.invalidateQueries({ queryKey: ['ganadores', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['participantes', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['sorteo', sorteoId] })
      toast.success(data.mensaje)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al deshacer último ganador')
    },
  })
}

export function useDescargarGanadores() {
  return useMutation({
    mutationFn: async (sorteoId: string) => {
      const blob = await descargarGanadores(sorteoId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ganadores_${sorteoId}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      a.remove()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al descargar ganadores')
    },
  })
}

// === PREMIOS ===

export function usePremios(sorteoId: string) {
  return useQuery({
    queryKey: ['premios', sorteoId],
    queryFn: () => getPremios(sorteoId),
    enabled: !!sorteoId,
  })
}

export function usePremioActivo(sorteoId: string) {
  return useQuery({
    queryKey: ['premio-activo', sorteoId],
    queryFn: () => getPremioActivo(sorteoId),
    enabled: !!sorteoId,
  })
}

export function useCrearPremio() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, payload }: { sorteoId: string; payload: PremioCreatePayload }) =>
      crearPremio(sorteoId, payload),
    onSuccess: (_, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['premios', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premio-activo', sorteoId] })
      toast.success('Premio creado')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al crear premio')
    },
  })
}

export function useActualizarPremio() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, premioId, payload }: { sorteoId: string; premioId: string; payload: PremioUpdatePayload }) =>
      actualizarPremio(sorteoId, premioId, payload),
    onSuccess: (_, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['premios', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premio-activo', sorteoId] })
      toast.success('Premio actualizado')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al actualizar premio')
    },
  })
}

export function useEliminarPremio() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, premioId }: { sorteoId: string; premioId: string }) =>
      eliminarPremio(sorteoId, premioId),
    onSuccess: (data, { sorteoId }) => {
      queryClient.invalidateQueries({ queryKey: ['premios', sorteoId] })
      queryClient.invalidateQueries({ queryKey: ['premio-activo', sorteoId] })
      toast.success(data.mensaje)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Error al eliminar premio')
    },
  })
}
